/**
 * Equipment bindings + live port-values resource (Phase 3/4).
 *
 * The tag↔equipment-port mapping that makes equipment first-class: a
 * device tag feeds an entity's sensor-port / parameter slot, and
 * /port-values resolves each bound slot to the latest reading(s).
 */

import { api } from "@/lib/api-client";
import { collectAllPages } from "./paging";
import { qs } from "./qs";
import type { Paginated } from "./types";
import type { ThingTagInput } from "./things";

/** Mirrors twin.Binding. */
export interface TwinBinding {
  id: string;
  entity_id: string;
  port: string;
  tag_id: string;
  tag_name: string;
  unit: string;
  data_kind: string;
  /**
   * The bound tag's source ('sensor'|'computed'|'manual'|'import'|'ble'); 'manual' = operator hand-keyed, 'ble' = a
   * handheld probe, 'import' = a data source's reader wrote it (a machine's history or a report). Go-live keeps an
   * import's binding and a manual entry's, 'manual' or 'ble' (S4).
   */
  source: string;
  role: string;
  /**
   * Boundary-signal role (M8.9), orthogonal to `role`. One of
   * stream|cycle_start|cycle_end|pulse|threshold; 'stream' (default) is
   * ordinary telemetry. See docs/design/event-driven-boundaries.md.
   */
  purpose: string;
  /**
   * v2-capture genealogy role (migration 0065): what a discrete arrival on this tag
   * means to the capture processor — heat_start / charge_added / tap / active_pattern /
   * mould_produced / casting_produced / qc_result / heat_close. '' (default) = not a
   * genealogy source. This is what makes a binding a manual-entry field.
   */
  genealogy_role: string;
  /**
   * Set when the binding is VALID but cannot work as configured, which is a different
   * thing from being rejected.
   *
   * Today it carries exactly one case: a genealogy role that needs an OPEN HEAT, in an
   * org where no binding has `genealogy_role: "heat_start"` to open one. Every reading
   * on such a tag is filed as an orphan rather than assembling into genealogy, and the
   * failure is completely silent otherwise. Measured on staging 2026-08-20: three of
   * four orgs holding heat-dependent bindings had no opener, 16 bindings in total.
   *
   * Present and unrendered today. Showing it beside the success state is the difference
   * between a warning and a warning somebody sees.
   */
  capture_warning?: string;
  created_at: string;
}

/** The five binding purposes (M8.9). 'stream' is the telemetry default. */
export type BindingPurpose =
  | "stream"
  | "cycle_start"
  | "cycle_end"
  | "pulse"
  | "threshold";

/** One tag feeding a slot + its latest reading, in its tag's unit. Mirrors twin.PortFeed. */
export interface PortFeed {
  tag_id: string;
  tag_name: string;
  role: string;
  /**
   * The unit `value` is in: the feed's tag's own unit, "" for none (one series per port, S5). A port fed by several
   * sources reads its value in the port's unit while each feed keeps its own reading, so a feed is labelled with this,
   * never with the port's unit. Absent from an older server: read the port's unit then (feedUnit).
   */
  unit?: string;
  value?: number;
  time?: string;
  /**
   * Only on a port fed by two or more sources (one series per port): true on the source whose reading is the port's
   * value now, its source of record.
   */
  of_record?: boolean;
  /** Only on a port fed by two or more sources: what each source is. Its tag often has the other sources' name. */
  source_kind?: "live" | "history" | "report" | "manual";
}

/**
 * Resolved live value for a bound slot. Mirrors twin.PortValue. A port fed by two or more sources reads its source of
 * record's current value, in the port's unit, never a mean across the sources; a redundant probe is still averaged in.
 */
export interface PortValue {
  port: string;
  data_kind: string;
  unit: string;
  value?: number;
  time?: string;
  feeds: PortFeed[];
}

export interface AutoSuggestion {
  port: string;
  port_label: string;
  data_kind: string;
  candidates: { tag_id: string; tag_name: string; score: number }[];
}

/** A tag option for the binding picker (subset of the iot Tag shape). */
export interface TagOption {
  id: string;
  device_id: string;
  name: string;
  unit: string;
  data_kind: string;
  /**
   * Tag origin: 'sensor' | 'computed' | 'manual'. Returned by GET /v1/iot/tags.
   * Drives the dashboard live-series window — manual (hand-keyed) tags are
   * sparse, so they need a far wider lookback than the tight live-sensor window.
   */
  source?: string;
  semantic_tag?: string | null;
  /**
   * The Thing this tag publishes through (P6.5). `GET /v1/iot/tags` now
   * returns these so the binding combobox can group tags by Thing and
   * match on Thing name OR tag name. Optional until every row carries them.
   */
  thing_name?: string | null;
  thing_slug?: string | null;
  /**
   * The reading in the plant's words, as the machine page names it (sweep PR-E): the label of the machine port the tag
   * streams to, else the words of its own name. Charts, axes and tables print it instead of `name`. Absent from an
   * older server.
   */
  label?: string;
  /** The machine the reading is bound to; absent for a tag no machine binds. */
  machine?: string;
  /**
   * Polling / addressing / simulator fields (P-wave3) — present on
   * `GET /v1/iot/tags` rows so the Thing detail sheet can show + edit them.
   * Optional until every row carries them.
   */
  poll_sec?: number | null;
  /** Publish only when the value moves more than this; omitted when unset. */
  deadband?: number | null;
  /** The effective heartbeat in seconds (own or default); omitted when none. See ThingTag. */
  max_silence_sec?: number | null;
  /** True when `max_silence_sec` is the tag's own, not the default. */
  max_silence_own?: boolean;
  /** False when the gateway leaves the tag out (not read); missing means true. */
  acquire?: boolean;
  address?: string | null;
  simulate?: boolean;
  /**
   * Declared value range (operating band). Returned by `GET /v1/iot/tags`; the
   * detail sheet edits these, and changing them re-synthesizes a simulated
   * tag's profile so the generated wave scales to the new band. Optional until
   * every row carries them.
   */
  min_value?: number | null;
  max_value?: number | null;
  /**
   * VSync Modbus wire metadata echoed by `GET /v1/iot/tags` (additive): `p_id`
   * = stable legacy param id (VSync `P_ID`), `p_type` = wire type (1/2 int, 3
   * long, 4 float), `reg_count` = register count. Let the reconfigure editor
   * seed + round-trip a Thing's exact addressing. Optional until every row
   * carries them.
   */
  p_id?: number | null;
  p_type?: number | null;
  reg_count?: number | null;
}

/**
 * A device tag (`api/openapi.yaml` Tag). The canonical row behind the
 * binding picker + the Thing detail sheet's tag inventory. `data_kind` is
 * the canonical 9-kind enum (NOT the friendly `data_type` facade the
 * Add-a-Thing dialog exposes). `poll_sec` / `address` / `simulate` are the
 * P-wave3 mutable fields the detail sheet edits.
 */
export interface Tag {
  id: string;
  device_id: string;
  name: string;
  description?: string;
  unit?: string;
  min_value?: number | null;
  max_value?: number | null;
  data_kind: string;
  semantic_tag?: string | null;
  source?: string;
  poll_sec?: number | null;
  /** Publish only when the value moves more than this; omitted when unset. */
  deadband?: number | null;
  /** The effective heartbeat in seconds (own or default); omitted when none. See ThingTag. */
  max_silence_sec?: number | null;
  /** True when `max_silence_sec` is the tag's own, not the default. */
  max_silence_own?: boolean;
  /** False when the gateway leaves the tag out (not read); missing means true. */
  acquire?: boolean;
  address?: string | null;
  simulate?: boolean;
  /** VSync Modbus wire metadata now echoed by the read (additive). */
  p_id?: number | null;
  p_type?: number | null;
  reg_count?: number | null;
  created_at: string;
}

/** `POST /v1/iot/tags` body — create a tag on an existing Thing (`device_id`). */
export interface TagCreateBody {
  device_id: string;
  name: string;
  unit?: string;
  data_kind?: string;
  poll_sec?: number;
  address?: string;
  simulate?: boolean;
  /** 'sensor' (default) | 'computed' | 'manual'. 'manual' = operator hand-keyed (data-entry → readings → dashboard). */
  source?: string;
}

/**
 * `PATCH /v1/iot/tags/{id}` body. Every field optional (COALESCE on the
 * server). P-wave3 widened the M3 set (unit/data_kind/semantic_tag/min/max)
 * to also accept `poll_sec`, `address`, and `simulate`.
 *
 * `poll_sec` is whole seconds 1 to 86400 and `deadband` is 0 or more (0 clears
 * it); out of range is a 400 `invalid_poll_sec` / `invalid_deadband`.
 * `max_silence_sec` is the point's own heartbeat, 0 to 604800 seconds (0 clears
 * it back to the default; else 400 `invalid_max_silence_sec`), and `acquire`
 * false leaves the point out of the gateway config. Saving a tag of an
 * instrument under a gateway pushes that gateway its config.
 */
export interface TagUpdateBody {
  unit?: string;
  data_kind?: string;
  semantic_tag?: string;
  min_value?: number;
  max_value?: number;
  poll_sec?: number;
  deadband?: number;
  max_silence_sec?: number;
  acquire?: boolean;
  address?: string;
  simulate?: boolean;
}

/** Latest reading for a tag (`api/openapi.yaml` TagLatest). */
export interface TagLatest {
  tag_id: string;
  device_id: string;
  metric: string;
  value: number;
  /** Zero-value timestamp ("0001-01-01T00:00:00Z") when no readings yet. */
  time: string;
  quality_flag?: string | null;
}

/** One tag's latest reading from the BATCHED `POST /v1/iot/tags:latest`.
 *  `value`/`time` are null when the tag has never reported (metadata-only). */
export interface BatchLatestItem {
  tag_id: string;
  metric: string;
  value: number | null;
  time: string | null;
  quality_flag?: string | null;
}

/** One point in a tag's time-series (`GET /v1/iot/tags/{id}/values`). */
export interface TagValuesPoint {
  t: string;
  value?: number | null;
  label?: string | null;
  count?: number | null;
}

/** Time-series response for a scalar/numeric tag. */
export interface TagValues {
  tag_id: string;
  device_id: string;
  metric: string;
  data_kind: string;
  from: string;
  to: string;
  /** "raw" when the window held at most 500 readings and each is a point, else the aggregate of each bucket ("avg"). */
  agg?: string;
  /** "raw", or the bucket width the points are means of ("1 minute", "15m0s"). */
  interval?: string;
  points?: TagValuesPoint[];
}

/**
 * Device-tag resource — typed wrappers over the `/v1/iot/tags*` surface.
 * Powers the Thing detail sheet's tag inventory (list its tags, show each
 * tag's latest value, add / edit / remove a tag).
 */
export const tags = {
  /**
   * List a Thing's tags. `GET /v1/iot/tags?device_id={id}`, by name. With no page it asks for 200; the gateway
   * sheet's own tag list asks a page at a time (limit, offset) with a name filter (`q`), as the drawer does for an
   * instrument. A caller that needs every tag reads `things.allTags`.
   */
  listByDevice: (deviceId: string, page?: { limit?: number; offset?: number; q?: string }) =>
    api.get<Paginated<TagOption>>(
      `/v1/iot/tags${qs({ device_id: deviceId, limit: page?.limit ?? 200, offset: page?.offset, q: page?.q?.trim() })}`,
    ),
  /** Latest reading for a tag. `GET /v1/iot/tags/{id}/latest`. */
  latest: (tagId: string) => api.get<TagLatest>(`/v1/iot/tags/${tagId}/latest`),
  /** Latest reading for MANY tags in ONE request (`POST /v1/iot/tags:latest`).
   *  Collapses the dashboard's per-widget × per-tag latest-value fan-out into a
   *  single round-trip + a single indexed query. Unknown/cross-tenant ids are
   *  omitted from `items`. */
  batchLatest: (tagIds: string[]) =>
    api.post<Paginated<BatchLatestItem>>("/v1/iot/tags:latest", { tag_ids: tagIds }),
  /** Recent time-series for a tag. `GET /v1/iot/tags/{id}/values`. */
  values: (tagId: string, params?: { from?: string; to?: string; interval?: string }) =>
    api.get<TagValues>(`/v1/iot/tags/${tagId}/values${qs(params ?? {})}`),
  /** Create a tag on a Thing. `POST /v1/iot/tags` → 201 Tag. */
  create: (body: TagCreateBody) => api.post<Tag>("/v1/iot/tags", body),
  /** Patch mutable tag fields. `PATCH /v1/iot/tags/{id}` → 200 Tag. */
  update: (tagId: string, body: TagUpdateBody) =>
    api.patch<Tag>(`/v1/iot/tags/${tagId}`, body),
  /** Delete a tag. `DELETE /v1/iot/tags/{id}` → 204. */
  remove: (tagId: string) => api.delete<void>(`/v1/iot/tags/${tagId}`),
  /**
   * Atomically REPLACE a Thing's entire tag set — the write path for changing a
   * VSync's equipment type or re-importing its `config.txt`.
   * `POST /v1/things/{thing_id}/tags:replace` → 200 the new tag list.
   *
   * The body uses the SAME `data_type` facade as `POST /v1/things` (via
   * `ThingTagInput`), so the instrument templates + config-import output feed
   * straight in — no data_type→data_kind mapping on the client. `instrument_type`
   * records the applied equipment type (e.g. vmbd / vud / vccd) onto the Thing.
   */
  replace: (
    thingId: string,
    body: { tags: ThingTagInput[]; instrument_type?: string },
  ) => api.post<Paginated<Tag>>(`/v1/things/${thingId}/tags:replace`, body),
};

export const twinBindings = {
  list: (entityId?: string) =>
    api.get<Paginated<TwinBinding>>(`/v1/twin/bindings${qs({ entity_id: entityId })}`),
  create: (body: {
    entity_id: string;
    port: string;
    tag_id: string;
    role?: string;
    purpose?: BindingPurpose;
    /** Genealogy role — set on a manual-entry tag so it becomes a captured field. */
    genealogy_role?: string;
  }) => api.post<TwinBinding>("/v1/twin/bindings", body),
  remove: (id: string) => api.delete<void>(`/v1/twin/bindings/${id}`),
  autoSuggest: (entityId: string) =>
    api.post<{ entity_id: string; suggestions: AutoSuggestion[] }>(
      "/v1/twin/bindings/auto-suggest",
      { entity_id: entityId },
    ),
  portValues: (entityId: string) =>
    api.get<{ entity_id: string; ports: PortValue[] }>(
      `/v1/twin/entities/${entityId}/port-values`,
    ),
};

/**
 * List ALL of the active org's device tags (the binding and alias pickers, and the
 * dashboard's name lookups). The server clamps a page at 200, so the single `limit=500`
 * request this used to make silently cut an org past 200 tags short — a template plant
 * with imported history (the CWC rehearsal) is past it, and every picker and dashboard
 * name lookup then missed the rest.
 */
export async function listOrgTags(): Promise<Paginated<TagOption>> {
  const all = await collectAllPages<TagOption>((offset, limit) =>
    api.get<Paginated<TagOption>>(`/v1/iot/tags${qs({ limit, offset })}`),
  );
  return { items: all.items, total: all.total, limit: all.items.length, offset: 0 };
}
